import {
  ErrorModal,
  MesfModal,
  TimeService,
  useShiftNavigator,
} from "@dexteel/mesf-core";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  TextField,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { AssetAutocomplete } from "../../../../../shared/autocompletes/AssetAutocomplete";
import { DelayCategoryAutocomplete } from "../../../../../shared/autocompletes/DelayCategoryAutocomplete";
import { CommentsField } from "../../../../../shared/fields/CommentsField";
import { DelayCodeTreePicker } from "../../../../../shared/treepickers/DelayCodeTreePicker";
import {
  useJustifyDelayMutation,
  useUpsertDelayMutation,
} from "../../../hooks/mutations";
import { useDelayByIdQuery } from "../../../hooks/queries";
import type { DelayByShift } from "../../models/DelayByShift";

type Props = {
  showModal: "new" | "edit" | "delete" | "justify" | "split" | "";
  onHide: () => void;
  delayId: number | null;
  assetId?: number | null;
};

export const UpsertJustifyDelay = ({
  showModal,
  onHide,
  delayId,
  assetId,
}: Props) => {
  const [error, setError] = useState<string | null>(null);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [title, setTitle] = useState("");

  const { shiftInfo } = useShiftNavigator();

  const upsertMutation = useUpsertDelayMutation();
  const justifyMutation = useJustifyDelayMutation();

  const isModalOpen =
    showModal !== "" && showModal !== "delete" && showModal !== "split";

  const { data: delayData, isLoading } = useDelayByIdQuery(
    delayId,
    isModalOpen && showModal !== "new",
  );

  const { setValue, control, handleSubmit, reset, getValues } =
    useForm<DelayByShift>({
      defaultValues: {
        DelayId: null,
        AssetId: null,
        DelayCodeId: null,
        DelayCodeName: null,
        Start: null,
        End: null,
        Duration: 0,
        ShiftId: 0,
        Comments: "",
        OriginalDelayId: 0,
        OriginalStart: null,
        OriginalEnd: null,
        JustifiedByUser: "",
        JustifiedDateTime: null,
        DelayCategoryId: null,
        IsManual: false,
        DelayCategoryName: "",
        DelayCategoryColor: "",
        ShiftId_1: 0,
        ProductionDate: null,
        ProductionShiftId: 0,
        Shift: "",
        Crew: "",
      },
    });

  const [start, end] = useWatch({ control, name: ["Start", "End"] });

  // Populate form when delay data arrives (edit/justify)
  useEffect(() => {
    if (!isModalOpen) return;

    if (delayId && delayData) {
      setTitle(`${showModal.toUpperCase()} DELAY`);
      const defaultStart = shiftInfo?.CurrentStart || null;
      reset({
        DelayId: delayData.DelayId,
        AssetId: delayData.AssetId,
        DelayCodeId: delayData.DelayCodeId,
        Start: delayData.Start ?? defaultStart,
        End: delayData.End ?? null,
        DelayCategoryId: delayData.DelayCategoryId,
        IsManual: delayData.IsManual,
        Comments: delayData.Comments,
      });
    } else if (showModal === "new") {
      setTitle("NEW DELAY");
      const defaultStart = shiftInfo?.CurrentStart
        ? new Date(shiftInfo.CurrentStart)
        : null;
      const defaultEnd = defaultStart ? new Date(defaultStart) : null;
      if (defaultEnd) {
        defaultEnd.setHours(defaultEnd.getHours() + 1);
      }

      reset({
        DelayId: 0,
        Start: defaultStart,
        End: defaultEnd,
        IsManual: true,
      });

      if (assetId) {
        setValue("AssetId", assetId);
      }
    }
  }, [isModalOpen, showModal, delayId, delayData]);

  const duration = (() => {
    if (!start) return "";
    if (end) {
      const dur = moment.duration(moment(end).diff(moment(start)));
      const minutes = Math.floor(dur.asMinutes());
      const seconds = dur.seconds();
      return `${minutes}m ${seconds}s`;
    }
    const dur = moment.duration(moment().diff(moment(start)));
    const minutes = Math.floor(dur.asMinutes());
    const seconds = dur.seconds();
    return `${minutes}m ${seconds}s (Ongoing)`;
  })();

  const onSubmit: SubmitHandler<DelayByShift> = (data: DelayByShift) => {
    if (showModal === "justify") {
      justifyMutation.mutate(
        {
          delayId: data.DelayId!,
          delayCodeId: data.DelayCodeId ?? null,
          delayCategoryId: data.DelayCategoryId ?? null,
          comments: data.Comments ?? null,
        },
        {
          onSuccess: () => {
            setNotificationMessage("Delay was justified successfully");
            onHide();
          },
          onError: (err) => {
            setError(
              err instanceof Error ? err.message : "Failed to justify delay",
            );
          },
        },
      );
    } else {
      upsertMutation.mutate(
        {
          delayId: data.DelayId === 0 ? null : (data.DelayId ?? null),
          assetId: data.AssetId ?? null,
          start: data.Start ?? null,
          end: data.End ?? null,
          delayCodeId: data.DelayCodeId ?? null,
          delayCategoryId: data.DelayCategoryId ?? null,
          comments: data.Comments ?? null,
        },
        {
          onSuccess: () => {
            setNotificationMessage(
              "Delay was " +
                (showModal === "new" ? "created" : "edited") +
                " successfully",
            );
            onHide();
          },
          onError: (err) => {
            setError(
              err instanceof Error ? err.message : "Failed to save delay",
            );
          },
        },
      );
    }
  };

  const isSubmitLoading = upsertMutation.isPending || justifyMutation.isPending;
  const isDataLoading = isLoading && !!delayId && showModal !== "new";

  return (
    <>
      <MesfModal
        maxWidth="md"
        open={isModalOpen}
        handleClose={() => {
          onHide();
        }}
        id="upsertDelay"
        title={title}
      >
        {isDataLoading ? (
          <CenteredLazyLoading />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
              <>
                <Grid container spacing={2}>
                  <Grid item xs={4} md={4}>
                    <Controller
                      name="Start"
                      control={control}
                      rules={{
                        deps: ["End"],
                        required: "Start date is required",
                        validate: {
                          notAfterEnd: (value) => {
                            const endDate = getValues("End");
                            if (value && endDate && value > endDate) {
                              return "Start date cannot be after End date.";
                            }
                            return true;
                          },
                          notInFuture: (value) => {
                            if (value && value > new Date()) {
                              return "Start date cannot be in the future.";
                            }
                            return true;
                          },
                        },
                      }}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <DateTimePicker
                          disabled={showModal === "justify"}
                          label="Delay Start"
                          timezone={TimeService.getInstance().getServerTimeZone()}
                          format="MM/DD/YYYY HH:mm:ss"
                          ampm={false}
                          value={field.value ? moment(field.value) : null}
                          onChange={field.onChange}
                          slotProps={{
                            textField: {
                              error: !!fieldError,
                              helperText: fieldError?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} md={4}>
                    <Controller
                      name="End"
                      control={control}
                      rules={{
                        deps: ["Start"],
                        validate: {
                          notBeforeStart: (value) => {
                            const startDate = getValues("Start");
                            if (value && startDate && value < startDate) {
                              return "End date cannot be before Start date.";
                            }
                            return true;
                          },
                          notInFuture: (value) => {
                            if (value && value > new Date()) {
                              return "End date cannot be in the future.";
                            }
                            return true;
                          },
                          notSameAsStart: (value) => {
                            const startDate = getValues("Start");
                            if (
                              value &&
                              startDate &&
                              moment(value).isSame(moment(startDate))
                            ) {
                              return "End date cannot be the same as Start date.";
                            }
                            return true;
                          },
                        },
                      }}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <DateTimePicker
                          disabled={showModal === "justify"}
                          label="Delay End"
                          format="MM/DD/YYYY HH:mm:ss"
                          ampm={false}
                          timezone={TimeService.getInstance().getServerTimeZone()}
                          value={field.value ? moment(field.value) : null}
                          onChange={field.onChange}
                          slotProps={{
                            textField: {
                              error: !!fieldError,
                              helperText: fieldError?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} md={4}>
                    <TextField
                      label="Duration (min)"
                      disabled
                      variant="outlined"
                      fullWidth
                      value={duration}
                      autoComplete="off"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4} md={4}>
                    <Controller
                      name="AssetId"
                      control={control}
                      rules={{ required: "Asset is required" }}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <AssetAutocomplete
                          label="Asset"
                          value={assetId || field.value}
                          onChange={field.onChange}
                          fieldError={fieldError}
                          disabled
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} md={4}>
                    <Controller
                      name="DelayCodeId"
                      control={control}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <DelayCodeTreePicker
                          label="Code"
                          value={field.value}
                          onChange={(value, defaultCategoryId) => {
                            field.onChange(value);
                            setValue("DelayCategoryId", defaultCategoryId);
                          }}
                          assetId={assetId ?? null}
                          fieldError={fieldError}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} md={4}>
                    <Controller
                      name="DelayCategoryId"
                      control={control}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <DelayCategoryAutocomplete
                          value={field.value}
                          onChange={field.onChange}
                          assetId={assetId ?? null}
                          fieldError={fieldError}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item md={12} xs={12}>
                    <Controller
                      name="Comments"
                      control={control}
                      render={({
                        field,
                        fieldState: { error: fieldError },
                      }) => (
                        <CommentsField
                          value={field.value}
                          onChange={field.onChange}
                          fieldError={fieldError}
                          getValues={getValues}
                          rows={{ minRows: 3, maxRows: 3 }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={6}>
                    <Controller
                      name="IsManual"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={showModal === "new" || field.value}
                              disabled={true}
                            />
                          }
                          label="Manual Delay"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </>
            </MesfModal.Content>
            <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item md={3} xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="inherit"
                    onClick={() => onHide()}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item md={3} xs={12}>
                  <Button
                    fullWidth
                    startIcon={
                      isSubmitLoading && <CircularProgress size="1rem" />
                    }
                    disabled={isSubmitLoading}
                    variant="contained"
                    color="primary"
                    type="submit"
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </MesfModal.Actions>
          </form>
        )}
      </MesfModal>
      <ErrorModal error={error ?? ""} onHide={() => setError(null)} />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
    </>
  );
};
